import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo } from 'react';
import type { NoteData } from 'components/Notes/types';
import type { Note } from 'pages/ValuationsAllocation/types';

interface UseValuationNotesCacheParams {
  cachedNotes?: Note[] | null;
  notes: NoteData[] | null;
  notesHasChanged: boolean;
  panelId?: string;
  setCachedNotes?: Dispatch<SetStateAction<Note[]>> | null;
  setNotes: (notes: NoteData[]) => void;
  setNotesHasChanged: Dispatch<SetStateAction<boolean>>;
}

const matchesPersistedNotes = (localNotes: NoteData[], persistedNotes: NoteData[]) => {
  if (localNotes.length !== persistedNotes.length) {
    return false;
  }

  return localNotes.every((localNote, index) => {
    const persistedNote = persistedNotes[index];
    if (!persistedNote) {
      return false;
    }

    if (!localNote.temp_id) {
      return localNote.id === persistedNote.id;
    }

    return (
      persistedNote.id != null &&
      persistedNote.temp_id == null &&
      localNote.content === persistedNote.content &&
      localNote.is_internal === persistedNote.is_internal &&
      localNote.page_type === persistedNote.page_type
    );
  });
};

export const useValuationNotesCache = ({
  cachedNotes,
  notes,
  notesHasChanged,
  panelId,
  setCachedNotes,
  setNotes,
  setNotesHasChanged,
}: UseValuationNotesCacheParams) => {
  const cachedNote = useMemo(
    () => (panelId ? cachedNotes?.find(noteEntry => noteEntry.panelId === panelId) : undefined),
    [cachedNotes, panelId]
  );
  const cachedPanelNotes = cachedNote?.notes ?? null;
  const shouldReplaceTemporaryNotes =
    notesHasChanged &&
    cachedNote?.notesHasChanged === false &&
    notes !== null &&
    cachedPanelNotes !== null &&
    notes.some(note => note.temp_id) &&
    matchesPersistedNotes(notes, cachedPanelNotes);

  // Rehydrate local approach notes when returning to a tab before the valuation has been saved.
  useEffect(() => {
    if (!panelId || notes !== null || !cachedPanelNotes) {
      return;
    }

    setNotes(cachedPanelNotes);
    setNotesHasChanged(Boolean(cachedNote?.notesHasChanged));
  }, [cachedNote?.notesHasChanged, cachedPanelNotes, notes, panelId, setNotes, setNotesHasChanged]);

  // Replace the active tab's temporary note ids once the parent cache receives the save response.
  useEffect(() => {
    if (!shouldReplaceTemporaryNotes || !cachedPanelNotes) {
      return;
    }

    setNotes(cachedPanelNotes);
    setNotesHasChanged(false);
  }, [cachedPanelNotes, setNotes, setNotesHasChanged, shouldReplaceTemporaryNotes]);

  // Keep the parent valuation cache current so unmounted approach tabs do not lose drafts.
  useEffect(() => {
    if (!panelId || !setCachedNotes || notes === null || shouldReplaceTemporaryNotes) {
      return;
    }

    setCachedNotes(previousNotes => {
      const currentEntry = previousNotes.find(noteEntry => noteEntry.panelId === panelId);
      if (currentEntry?.notes === notes && currentEntry.notesHasChanged === notesHasChanged) {
        return previousNotes;
      }

      const otherNotes = previousNotes.filter(noteEntry => noteEntry.panelId !== panelId);
      return [
        ...otherNotes,
        {
          notes,
          panelId,
          notesHasChanged,
        },
      ];
    });
  }, [notes, notesHasChanged, panelId, setCachedNotes, shouldReplaceTemporaryNotes]);

  return {
    notes: notes ?? cachedPanelNotes,
  };
};
